import type { ReactNode } from "react";

import {
    getCpcSelectedText,
    getVolumeSelectedText,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import { translate } from "app/filters/i18nFilter";
import { changeFilterItems } from "app/common/keyword-research/landing-pages/landingPagesFilterConsts";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import allSearchEngines from "app/pages/website-analysis/constants/searchEngines";
import allSearchTypes from "app/pages/website-analysis/constants/searchTypes";
import { getPresetsBoxesConfig } from "app/common/keyword-research/website-keywords/competitiveFilterPresets";

import { getItemDisplayValue } from "./TableLandingPageDashboardConst";
import { filterTrafficPresetItems } from "./TableWebsiteKeywordsDashboardWidgetFilters";

export enum EWebsiteKeywordsTableFilters {
    MULTI_INCLUDE_EXCLUDE_KEYWORDS = "multiIncludeExcludeKeywords",
    MULTI_INCLUDE_EXCLUDE_WEBSITES = "multiIncludeExcludeWebsites",
    INCLUDE_BRANDED = "IncludeBranded",
    INCLUDE_NONE_BRANDED = "IncludeNoneBranded",
    SEARCH_TYPE = "source",
    SEARCH_ENGINES = "family",
    CLICKS_CHANGE = "Change",
    VALUE = "value",
    CPC = "cpc",
    TRAFFIC_PRESET = "trafficPreset",
}

const URLS = "URLs";
const KEYWORDS = "Keywords";

interface IIncludeExcludeItem {
    type: "Include" | "Exclude";
    matchType: string;
    items: Array<{ type: "website" | "keywords"; value: string }>;
}

export const parseIncludeExcludeForTooltip = async (value: IIncludeExcludeItem[], type: string) => {
    const resultString = await Promise.all(value.map((item) => getItemDisplayValue(item, type)));
    const divCount = resultString.length;
    const divElements: ReactNode = resultString.map((item, index) => <div key={index}>{item}</div>);
    return { divElements, divCount };
};

export type FilterLogicFunction = (filters: any) => Promise<{ divElements: ReactNode; divCount: number } | null>;

export const WebsiteKeywordsWidgetFiltersMap: Record<EWebsiteKeywordsTableFilters, FilterLogicFunction> = {
    [EWebsiteKeywordsTableFilters.MULTI_INCLUDE_EXCLUDE_KEYWORDS]: async (filters) =>
        filters.multiIncludeExcludeKeywords
            ? await parseIncludeExcludeForTooltip(JSON.parse(filters.multiIncludeExcludeKeywords), KEYWORDS)
            : null,
    [EWebsiteKeywordsTableFilters.MULTI_INCLUDE_EXCLUDE_WEBSITES]: async (filters) =>
        filters.multiIncludeExcludeWebsites
            ? await parseIncludeExcludeForTooltip(JSON.parse(filters.multiIncludeExcludeWebsites), URLS)
            : null,
    [EWebsiteKeywordsTableFilters.SEARCH_TYPE]: async (filters) => {
        const sources = filters.source?.split(FILTER_DEFAULT_SEPARATOR);
        const selectedSearchType = sources?.map((source) => allSearchTypes.find((type) => type.id === source)?.text);
        return selectedSearchType?.length
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.landing.page.table.search.type.filter", {
                      source: selectedSearchType.join(", "),
                  }),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.SEARCH_ENGINES]: async (filters) => {
        const families = filters.family?.split(FILTER_DEFAULT_SEPARATOR).map((item) => item.replace(/["\\]/g, ""));
        const selectedSearchEngines = families?.map(
            (family) => allSearchEngines.find((searchEngine) => searchEngine.id === family)?.text,
        );
        return selectedSearchEngines?.length
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.landing.page.table.search.engines.filter", {
                      family: selectedSearchEngines.join(", "),
                  }),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.CLICKS_CHANGE]: async (filters) => {
        const change = changeFilterItems.find((changeItem) => changeItem.id === filters.Change)?.text;
        return change
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.landing.page.table.clicks.change.filter", {
                      change,
                  }),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.INCLUDE_BRANDED]: async (filters) => {
        return filters.IncludeBranded === "true" && filters.IncludeNoneBranded !== "true"
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.landing.page.table.include.branded"),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.INCLUDE_NONE_BRANDED]: async (filters) => {
        return filters.IncludeNoneBranded === "true" && filters.IncludeBranded !== "true"
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.landing.page.table.include.none.branded"),
              }
            : { divCount: 0, divElements: null };
    },

    [EWebsiteKeywordsTableFilters.VALUE]: async (filters) => {
        const volumeObj = { toValue: filters.volumeToValue, fromValue: filters.volumeFromValue };
        const value = getVolumeSelectedText(volumeObj);
        return filters.volumeToValue || filters.volumeFromValue
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.website.keywords.page.table.filter.volume", { value }),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.CPC]: async (filters) => {
        const cpcObj = { toValue: filters.cpcToValue, fromValue: filters.cpcFromValue };

        const value = getCpcSelectedText(cpcObj);

        return filters.cpcFromValue || filters.cpcToValue
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.website.keywords.page.table.filter.cpc", { value }),
              }
            : { divCount: 0, divElements: null };
    },
    [EWebsiteKeywordsTableFilters.TRAFFIC_PRESET]: async (filters) => {
        const { trafficPreset, keys, country, webSource } = filters;
        const trafficPresetOptions = getPresetsBoxesConfig(
            translate,
            keys.split(",")[0],
            keys.split(",").length > 1,
            webSource,
            country === 999 || country === "999",
        );

        const trafficPresetItems = filterTrafficPresetItems(trafficPresetOptions);
        const preset = trafficPresetItems.find((filter) => filter.id === trafficPreset);

        return trafficPreset
            ? {
                  divCount: 1,
                  divElements: translate("dashboard.metrics.website.keywords.page.table.filter.trafficPreset", {
                      value: preset.text,
                  }),
              }
            : { divCount: 0, divElements: null };
    },
};

export const WebsiteKeywordsTableWidgetAPIParams: EWebsiteKeywordsTableFilters[] = [
    EWebsiteKeywordsTableFilters.MULTI_INCLUDE_EXCLUDE_KEYWORDS,
    EWebsiteKeywordsTableFilters.MULTI_INCLUDE_EXCLUDE_WEBSITES,
    EWebsiteKeywordsTableFilters.INCLUDE_BRANDED,
    EWebsiteKeywordsTableFilters.INCLUDE_NONE_BRANDED,
    EWebsiteKeywordsTableFilters.SEARCH_TYPE,
    EWebsiteKeywordsTableFilters.SEARCH_ENGINES,
    EWebsiteKeywordsTableFilters.CLICKS_CHANGE,
    EWebsiteKeywordsTableFilters.VALUE,
    EWebsiteKeywordsTableFilters.CPC,
    EWebsiteKeywordsTableFilters.TRAFFIC_PRESET,
];
